import type { TableSort } from './tableSearch';

/** A column's sort value; `undefined` means "no value" and always sorts last. */
export type SortValue<T> = (row: T) => string | number | undefined;

/**
 * Sorts rows already loaded in full (a screen with no paging) by one column.
 * A missing value sorts last in either direction; equal values fall back to
 * `tiebreak` so the order is stable. With no sort, or an unknown key, the rows
 * come back as they are.
 */
export function sortRows<T>(
  rows: T[],
  sort: TableSort | null,
  values: Record<string, SortValue<T>>,
  tiebreak: (a: T, b: T) => number,
): T[] {
  const value = sort && values[sort.key];
  if (!sort || !value) return rows;
  const dir = sort.dir === 'desc' ? -1 : 1;
  return [...rows].sort((a, b) => {
    const va = value(a);
    const vb = value(b);
    if (va === undefined || vb === undefined) return va === vb ? tiebreak(a, b) : va === undefined ? 1 : -1;
    if (va === vb) return tiebreak(a, b);
    return (va < vb ? -1 : 1) * dir;
  });
}

/**
 * The sort a client-sorted table shows: the URL's, else `fallback`. Clearing a
 * sort returns to the fallback; clicking the fallback's column while it is in
 * force flips its direction instead of "clearing" back to itself.
 */
export function defaultedSort(
  urlSort: TableSort | null,
  fallback: TableSort,
  setSort: (next: TableSort | null) => void,
): { sort: TableSort; onSortChange: (next: TableSort | null) => void } {
  const sort = urlSort ?? fallback;
  return {
    sort,
    onSortChange: (next) =>
      setSort(
        next ??
          (!urlSort && sort.key === fallback.key
            ? { key: fallback.key, dir: fallback.dir === 'desc' ? 'asc' : 'desc' }
            : null),
      ),
  };
}
